import { useState, useEffect, useCallback } from "react";
import { Container } from "react-bootstrap";
import { CurrencyDollar } from "react-bootstrap-icons";
import { HeaderButton } from "../shared";
import ToastMessage from "../../ToastMessage";
import { WithApiAccess } from "../../../lib/HigherOrderComponents/WithApiAccess";
import { withHeaderMessages } from "../../../higherOrderComponents/WithHeaderMessages";
import { CalculateBonusModal } from "../ContractCommissions/CalculateBonusModal";
import { NewRunModal } from "./NewRunModal";
import { UnclaimedPool } from "./UnclaimedPool";
import { usePayoutRuns } from "./hooks/usePayoutRuns";
import { useCustomerProfile } from "../hooks/useCustomerProfile";
import { PageHeader } from "./components/PageHeader";
import { CustomerSelect } from "./components/CustomerSelect";
import { EmptyState } from "./components/EmptyState";
import { RunsTable } from "./components/RunsTable";
import {
  createInitialToastState,
  createToastState,
  hideToastState,
} from "./utils/toastHelpers";
import "../Commissions.css";

const DEFAULT_PAGE_SIZE = 10;

const PayoutRuns = (props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [toast, setToast] = useState(createInitialToastState);

  const showToast = (message, type = "success", action = "info") => {
    setToast(createToastState(message, type, action));
  };

  const hideToast = () => {
    setToast(hideToastState);
  };

  const {
    customers,
    isLoading: isLoadingCustomers,
    load: loadCustomers,
  } = useCustomerProfile({
    api: props.api,
    onError: () => showToast("Could not load customers.", "danger", "error"),
  });

  const [customerCode, setCustomerCode] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [isCalculatingBonus, setIsCalculatingBonus] = useState(false);
  const [unclaimedRefreshToken, setUnclaimedRefreshToken] = useState(0);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const handleSearch = useCallback((newQuery) => {
    setQuery(newQuery ? newQuery.trim() : "");
    setPage(1);
  }, []);

  useEffect(() => {
    loadCustomers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (customers?.length === 1) setCustomerCode(customers[0].code);
  }, [customers]);

  const resolvedCustomerCode =
    customers?.length === 1 ? customers[0].code : customerCode || undefined;

  useEffect(() => {
    setPage(1);
  }, [resolvedCustomerCode]);

  const {
    runs,
    totalRecords,
    totalPages,
    isLoading,
    hasLoaded,
    fetchRuns,
    createPayoutRun,
  } = usePayoutRuns({
    api: props.api,
    customerCode: resolvedCustomerCode,
    query,
    page,
    resultsPerPage: pageSize,
    onError: () => showToast("Could not load payout runs.", "danger", "error"),
  });

  const changePaging = useCallback((newPage, newPageSize) => {
    setPage(newPage);
    setPageSize(newPageSize);
  }, []);

  const createRun = useCallback(
    async ({ name, start, end }) => {
      setIsCreating(true);
      try {
        await createPayoutRun({
          customerCode: resolvedCustomerCode,
          name,
          startDate: start,
          endDate: end,
        });
        showToast(`${name} created.`, "success", "created");
        setUnclaimedRefreshToken((x) => x + 1);
      } catch {
        showToast("Could not create payout run.", "danger", "error");
      } finally {
        setShowModal(false);
        setIsCreating(false);
        fetchRuns(resolvedCustomerCode);
      }
    },
    [createPayoutRun, fetchRuns, resolvedCustomerCode, props.api],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateBonus = useCallback(
    async (request) => {
      setIsCalculatingBonus(true);
      try {
        await props.api.post("/api/commission/calculate-bonus", request, {
          suppressGlobalError: true,
        });
        showToast(
          "Bonus calculation submitted successfully.",
          "success",
          "created",
        );
        setUnclaimedRefreshToken((x) => x + 1);
        setShowBonusModal(false);
      } catch {
        showToast(
          "Unable to submit bonus calculation. Please try again.",
          "danger",
          "error",
        );
      } finally {
        setShowBonusModal(false);
        setIsCalculatingBonus(false);
      }
    },
    [props.api],
  );

  const hasSearch = query.length > 0;
  const showEmpty = hasLoaded && !isLoading && totalRecords === 0 && !hasSearch;
  const showTable = !showEmpty;

  return (
    <div className="commissions-scope min-vh-100 text-body">
      <ToastMessage
        message={toast.message}
        show={toast.show}
        handleToast={hideToast}
        type={toast.type}
        action={toast.action}
      />
      <Container className="py-2 py-sm-3">
        <PageHeader title="Payout runs">
          {!isLoadingCustomers && (
            <CustomerSelect
              customers={customers}
              value={customerCode}
              onChange={setCustomerCode}
            />
          )}
          <HeaderButton
            text="Calculate bonus"
            variant="outline-primary"
            onClick={() => setShowBonusModal(true)}
            disabled={isLoadingCustomers || isCalculatingBonus}
            icon={<CurrencyDollar size={14} />}
            className="commission-btn--rounded commission-btn--icon commission-btn--bonus"
          />
          <HeaderButton
            text="New payout run"
            variant="primary"
            onClick={() => setShowModal(true)}
            isSubmitting={isCreating}
          />
        </PageHeader>

        <UnclaimedPool
          api={props.api}
          customerCode={resolvedCustomerCode}
          refreshToken={unclaimedRefreshToken}
          onError={() =>
            showToast("Could not load unclaimed records.", "danger", "error")
          }
        />

        {showEmpty && (
          <EmptyState
            title="No payout runs found"
            description="Create a new run to get started."
          />
        )}

        {showTable && (
          <RunsTable
            runs={runs}
            query={query}
            onSearch={handleSearch}
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            totalPages={totalPages}
            onPagingChange={changePaging}
            isLoading={isLoading || isLoadingCustomers}
          />
        )}
      </Container>

      <NewRunModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onCreate={createRun}
        isCreating={isCreating}
      />

      <CalculateBonusModal
        show={showBonusModal}
        onHide={() => setShowBonusModal(false)}
        customers={customers ?? []}
        loading={isLoadingCustomers}
        submitting={isCalculatingBonus}
        onSubmit={calculateBonus}
        initialCustomerCode={resolvedCustomerCode}
      />
    </div>
  );
};

export default withHeaderMessages(WithApiAccess(PayoutRuns));
